// web/src/api/ticket/enhance.ts —— 工单增强（类型枚举 / AI 预填 / 催办提级 / 关联 / 附件 / 表单与动态字段）
// 拆自 web/src/api/ticket.ts 的同名分区（内容原样搬运）；由 web/src/api/ticket.ts 以 export * 聚合，导出面对调用方不变。

import { del, get, post, put } from '../../request';
import { toPage, type Page, type PageQuery } from '../types';

import type { VocabTreeNode } from './templates';

// ---------- 工单增强 ----------

/** 工单类型受控枚举（v1.8 扩展业务类：宽带/线路/升级/移机/新装/销户/携转，供 AI 自动建单与表单下拉单点维护） */
export const TICKET_TYPE_OPTIONS: { label: string; value: string }[] = [
  { label: '咨询', value: 'consult' },
  { label: '投诉', value: 'complaint' },
  { label: '故障', value: 'fault' },
  { label: '宽带报修', value: 'broadband' },
  { label: '电话线路', value: 'line_fault' },
  { label: '业务升级', value: 'business_upgrade' },
  { label: '宽带移机', value: 'broadband_move' },
  { label: '宽带新装', value: 'broadband_install' },
  { label: '销户', value: 'cancel_account' },
  { label: '携号转网', value: 'port_transfer' },
  { label: '其他', value: 'other' },
];

export interface PrefillDraft {
  title: string;
  type: string;
  /** v1.11 业务分类 code（ai_intent_vocab 词表，AI 推荐可改） */
  category?: string;
  priority: string;
  /** v1.11 详细问题描述（AI 分类依据） */
  description?: string;
  content: string;
  /** v1.11 推荐审核流程模板 id（0/undefined=无推荐） */
  workflow_template_id?: number;
  /** v1.16 扩展字段 AI 预填值（key=field_key，坐席可改） */
  field_values?: Record<string, string>;
}

/** POST /tickets/prefill：AI 预填建单草稿（source=cdr|chat） */
export function prefillTicket(source: 'cdr' | 'chat', refId: number): Promise<PrefillDraft> {
  return post('/tickets/prefill', { source, ref_id: refId });
}

/** 催办 / 提级 */
export function urgeTicket(id: number) {
  return post(`/tickets/${id}/urge`, {});
}
export function escalateTicket(id: number) {
  return post(`/tickets/${id}/escalate`, {});
}

/** 关联通话/会话 */
export function linkTicket(id: number, v: { cdr_id?: number; chat_session_id?: number }) {
  return post(`/tickets/${id}/link`, v);
}

/** 附件 */
export interface TicketAttachment {
  id: number;
  ticket_id: number;
  name: string;
  object_key?: string;
  size: number;
  mime_type?: string;
  created_at?: string;
}

export function uploadTicketAttachment(id: number, file: File) {
  const fd = new FormData();
  fd.append('file', file);
  return post(`/tickets/${id}/attachments`, fd, { timeout: 60000 });
}
export function listTicketAttachments(id: number): Promise<TicketAttachment[]> {
  return get(`/tickets/${id}/attachments`);
}
export function deleteTicketAttachment(id: number, attId: number) {
  return del(`/tickets/${id}/attachments/${attId}`);
}

/** 附件打开/下载（鉴权代理流，?token= 由 authUrl 注入） */
export function ticketAttachmentUrl(id: number, attId: number) {
  return `/api/v1/tickets/${id}/attachments/${attId}`;
}

export interface TicketComment {
  id?: number;
  author_name?: string;
  content: string;
  created_at?: string;
  [k: string]: any;
}

export interface Ticket {
  id: number;
  title: string;
  type?: string;
  /** v1.11 业务分类 code（AI 分类/模板路由） */
  category?: string;
  /** v1.11 详细问题描述（AI 分类依据，坐席可读） */
  description?: string;
  /**
   * 结构化标签（JSON 数组字符串）：AI 小结 tags 透传，后端存 Ticket.Tags；
   * 前端从详情渲染 PillTag（不再展示"标签：['xxx']"这种技术态原文）。
   */
  tags?: string[] | string;
  priority?: string;
  status?: string;
  customer_id?: number | null;
  /** 创建人 id（工单详情权限提醒判断：创建人/指派人/主管/admin 可操作） */
  creator_id?: number;
  assignee_id?: number | null;
  content?: string;
  cdr_id?: number | null;
  chat_session_id?: number | null;
  follow_up?: boolean; // 需回访：关闭时自动生成「工单回访」外呼任务
  callback_task_id?: number; // 详情携带：回访外呼任务 id（0/undefined=未生成）
  // 回访外呼任务状态（回访闭环可视；undefined=未生成）
  callback_task?: {
    id: number;
    name: string;
    status: string; // draft|queued|running|paused|done
    total: number;
    called: number;
    connected: number;
  } | null;
  workflow_instance_id?: number; // 已发起的审核流程实例（发起时选择流程后自动创建）
  workflow_template_id?: number | null; // 所选审核流程模板 id（编辑预选用）
  workflow_template_name?: string; // 详情携带：所选审核流程模板名
  comments?: TicketComment[]; // 详情内嵌评论数组
  // SLA
  sla_due_at?: string;
  resolved_at?: string;
  closed_at?: string;
  completed_at?: string; // 审核流程全部通过时间（completed 已完成状态）
  overdue?: boolean; // 列表/看板项携带
  /** 无人负责：非终态且既无处理人也无在途流程（列表/看板项携带，见工单列表「无人负责」标记） */
  unowned?: boolean;
  /** 必填采集字段未收齐（草稿态专有：true 的草稿需补齐后再提交，见列表「必填缺失」列） */
  incomplete?: boolean;
  // v1.16 动态字段：派单负责技能组 / 字段值 / 字段定义
  responsible_skill_group_id?: number | null;
  field_values?: Record<string, string>;
  field_defs?: TicketField[];
  created_at?: string;
  [k: string]: any;
}

/** 表单态：customerId / assigneeId 为 camelCase */
export interface TicketForm {
  title: string;
  type?: string;
  /** v1.11 业务分类 code（ai_intent_vocab 词表；v1.16 级联叶子 code） */
  category?: string;
  /** v1.11 详细问题描述 */
  description?: string;
  priority?: string;
  customerId?: number;
  assigneeId?: number;
  cdrId?: number; // 关联通话 CDR
  /** 关联在线会话（工作台 Tab 行「建工单」/ 会话头部「新建工单」） */
  chatSessionId?: number;
  /** 关联在途通话 uuid（CDR 落库前建单；落库后自动绑定 cdr_id） */
  uuid?: string;
  /** 客户手机号（人工建单自动关联：后端按号查/建客户并写 customer_id） */
  customerPhone?: string;
  followUp?: boolean; // 需回访
  workflowTemplateId?: number; // 发起时选择走哪个审核流程
  content: string;
  /** v1.16 动态字段值（字段自定义，key=field_key） */
  dynamicFields?: Record<string, string>;
}

/** v1.16 工单动态字段定义（字段自定义「搭积木」） */
export interface TicketField {
  id?: number;
  field_key: string;
  label: string;
  /** input|textarea|select|radio|cascade */
  type: 'input' | 'textarea' | 'select' | 'radio' | 'cascade';
  /** select/radio 选项 JSON：数组或 [{value,label}]；cascade 复用分类树 */
  options?: string;
  placeholder?: string;
  required?: boolean;
  sort?: number;
  enabled?: boolean;
  created_by?: number;
  created_at?: string;
}

/** GET /ticket-fields（admin） */
export function listTicketFields(): Promise<TicketField[]> {
  return get('/ticket-fields');
}

/** GET /ticket-fields/active（启用字段，坐席/任意登录用户可读：建单/编辑表单动态渲染） */
export function listEnabledTicketFields(): Promise<TicketField[]> {
  return get('/ticket-fields/active');
}
export function createTicketField(f: Partial<TicketField>) {
  return post('/ticket-fields', f);
}
export function updateTicketField(id: number, f: Partial<TicketField>) {
  return put(`/ticket-fields/${id}`, f);
}
export function deleteTicketField(id: number) {
  return del(`/ticket-fields/${id}`);
}

/** v1.16 扩展字段选项解析（兼容 ["v"] 与 [{"value","label"}] 两种历史形态），供表单与详情共用 */
export function parseTicketFieldOptions(options?: string): { label: string; value: string }[] {
  if (!options) return [];
  try {
    const raw = JSON.parse(options);
    if (Array.isArray(raw)) {
      return raw.map((it: any) =>
        typeof it === 'string'
          ? { label: it, value: it }
          : { label: String(it?.label ?? it?.value ?? ''), value: String(it?.value ?? '') }
      );
    }
  } catch {
    /* 非法 options 忽略 */
  }
  return [];
}

/**
 * 分类树按 code 递归查节点名（cascade 字段落库的是分类 code，详情需还原分类名）；
 * 未命中返回空串，由调用方决定回退展示。
 */
export function vocabNameByCode(nodes: VocabTreeNode[], code: string): string {
  for (const n of nodes) {
    if (n.code === code) return n.name;
    const hit = n.children?.length ? vocabNameByCode(n.children, code) : '';
    if (hit) return hit;
  }
  return '';
}

/**
 * v1.16 扩展字段值 → 展示文案：select/radio 落库的是选项 value（如 home）、
 * cascade 落库的是分类 code，表单里显示的都是中文（家庭宽带），详情直接渲染 value 会露出内部码；
 * cascade 的名称需传 categoryTree 才能还原；未命中的存量值原样返回（不猜中文、不留空）。
 */
export function ticketFieldDisplayValue(
  field: TicketField,
  value?: string | null,
  categoryTree?: VocabTreeNode[]
): string {
  const v = value === null || value === undefined ? '' : String(value);
  if (!v) return v;
  if (field.type === 'select' || field.type === 'radio') {
    return parseTicketFieldOptions(field.options).find((o) => o.value === v)?.label || v;
  }
  if (field.type === 'cascade' && categoryTree?.length) {
    return vocabNameByCode(categoryTree, v) || v;
  }
  return v;
}

